import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { Autocomplete, Box, Button, Card, LinearProgress, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';
import Page from '../../../components/Page';
import FromAndToDatePicker from '../../../components/mui/FromAndToDatePicker';
import RetentionThemeProvider from '../../retention/theme/RetentionThemeProvider';
import { RT } from '../../retention/theme/retentionTheme';
import { getCourseAvailable } from '../../../redux/features/batchSlice';
import { getAttendanceOverview, getBatchAttendance, getStudentAttendance } from '../../../redux/features/batchAttendanceAnalyticsSlice';
import { DISPLAY_FONT, HERO } from './components/ui';
import OverviewView from './OverviewView';
import BatchView from './BatchView';
import StudentView from './StudentView';

// Dashboard → Batch Attendance Analytics. Three levels, kept in the URL so back/forward and shared links work:
//   all batches  →  ?batch=<course_batch_id>  →  ?batch=<id>&student=<batch_assignment_id>
// Attendance comes from the marks saved on the batch academic calendar.

const BATCH_STATUSES = [
  { id: 1, name: 'Active' },
  { id: 2, name: 'Temporary Inactive' },
  { id: 3, name: 'Upcoming' },
  { id: 4, name: 'Completed' },
];

const RANGES = [
  { key: 'all', label: 'All time' },
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'last_3_months', label: 'Last 3 months' },
  { key: 'this_year', label: 'This year' },
  { key: 'custom', label: 'Custom range' },
];

const rangeDates = (key) => {
  const today = dayjs();
  switch (key) {
    case 'this_month':
      return [today.startOf('month'), today.endOf('month')];
    case 'last_month':
      return [today.subtract(1, 'month').startOf('month'), today.subtract(1, 'month').endOf('month')];
    case 'last_3_months':
      return [today.subtract(2, 'month').startOf('month'), today.endOf('month')];
    case 'this_year':
      return [today.startOf('year'), today.endOf('year')];
    default:
      return [null, null];
  }
};

// Runs a thunk whenever its params change; a newer request cancels the one still in flight.
// While a filter reloads the same batch/student the old numbers stay on screen; a different one starts blank.
const useAnalytics = (thunk, params, enabled, identity = 'overview') => {
  const dispatch = useDispatch();
  const [state, setState] = useState({ loading: false, data: null, identity: null });
  const key = JSON.stringify(params);
  useEffect(() => {
    if (!enabled) return undefined;
    setState((prev) => ({ loading: true, data: prev.identity === identity ? prev.data : null, identity }));
    const promise = dispatch(thunk(params));
    promise.then((action) => {
      if (action.meta?.aborted) return;
      setState({ loading: false, data: action.payload?.status === 'success' ? action.payload.data : null, identity });
    });
    return () => promise.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, key, enabled]);
  return state;
};

const Crumb = ({ label, onClick, current }) => (
  <Stack
    direction="row"
    alignItems="center"
    gap={0.5}
    onClick={current ? undefined : onClick}
    sx={{
      cursor: current ? 'default' : 'pointer',
      opacity: current ? 1 : 0.75,
      fontWeight: current ? 700 : 600,
      fontSize: 13,
      maxWidth: 260,
      '&:hover': current ? undefined : { opacity: 1, textDecoration: 'underline' },
    }}
  >
    <Typography component="span" sx={{ fontSize: 13, fontWeight: 'inherit' }} noWrap>
      {label}
    </Typography>
  </Stack>
);

export default function BatchAttendanceAnalytics() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const batchId = searchParams.get('batch');
  const studentId = searchParams.get('student');
  const view = studentId ? 'student' : batchId ? 'batch' : 'overview';

  const { allcourses = [] } = useSelector((state) => state.batch);
  const [course, setCourse] = useState('');
  const [status, setStatus] = useState(1);
  const [range, setRange] = useState('all');
  const [customFrom, setCustomFrom] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [customTo, setCustomTo] = useState(dayjs().format('YYYY-MM-DD'));

  useEffect(() => {
    dispatch(getCourseAvailable());
  }, [dispatch]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [batchId, studentId]);

  const dates = useMemo(() => {
    if (range === 'custom') return { from: customFrom || '', to: customTo || '' };
    const [from, to] = rangeDates(range);
    return { from: from ? from.format('YYYY-MM-DD') : '', to: to ? to.format('YYYY-MM-DD') : '' };
  }, [range, customFrom, customTo]);

  const overview = useAnalytics(getAttendanceOverview, { ...dates, course_available_id: course, course_batch_status: status }, view === 'overview');
  const batch = useAnalytics(getBatchAttendance, { batchId, ...dates }, view === 'batch' && Boolean(batchId), batchId);
  const student = useAnalytics(getStudentAttendance, { batchAssignmentId: studentId, ...dates }, view === 'student' && Boolean(studentId), studentId);

  const openBatch = (row) => setSearchParams({ batch: String(row.batchId) });
  const openStudent = (row) => setSearchParams({ batch: String(row.batchId || batchId || ''), student: String(row.batchAssignmentId) });
  const goOverview = () => setSearchParams({});

  const batchName = (view === 'student' ? student.data?.batch?.batchName : batch.data?.batch?.batchName) || (batchId ? `Batch #${batchId}` : '');
  const loading = view === 'overview' ? overview.loading : view === 'batch' ? batch.loading : student.loading;

  return (
    <RetentionThemeProvider>
      <Page title="Batch Attendance Analytics">
        <Box>
          <Stack gap={2.5}>
            <Card sx={{ overflow: 'hidden', border: 'none', borderRadius: 4, background: HERO, color: '#fff', position: 'relative' }}>
              <Box sx={{ position: 'absolute', width: 320, height: 320, borderRadius: '50%', right: -90, top: -160, bgcolor: 'rgba(255,255,255,.07)' }} />
              <Box sx={{ position: 'absolute', width: 180, height: 180, borderRadius: '50%', right: 160, bottom: -120, bgcolor: 'rgba(255,255,255,.05)' }} />
              <Stack direction={{ xs: 'column', md: 'row' }} gap={2} alignItems={{ md: 'center' }} sx={{ p: { xs: 2.5, md: 3 }, position: 'relative' }}>
                <Box
                  sx={{ width: 56, height: 56, borderRadius: 3.5, display: 'grid', placeItems: 'center', bgcolor: 'rgba(255,255,255,.16)', border: '1px solid rgba(255,255,255,.25)', flexShrink: 0 }}
                >
                  <Icon icon="solar:calendar-mark-bold-duotone" width={30} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" gap={0.75} sx={{ mb: 0.25 }} flexWrap="wrap">
                    <Crumb label="All batches" onClick={goOverview} current={view === 'overview'} />
                    {view !== 'overview' && (
                      <>
                        <Icon icon="solar:alt-arrow-right-linear" width={14} style={{ opacity: 0.6 }} />
                        <Crumb label={batchName} onClick={() => setSearchParams({ batch: batchId })} current={view === 'batch'} />
                      </>
                    )}
                    {view === 'student' && (
                      <>
                        <Icon icon="solar:alt-arrow-right-linear" width={14} style={{ opacity: 0.6 }} />
                        <Crumb label={student.data?.student?.studentName || 'Student'} current />
                      </>
                    )}
                  </Stack>
                  <Typography sx={{ fontFamily: DISPLAY_FONT, fontSize: { xs: 26, md: 32 }, fontWeight: 700, lineHeight: 1.15 }}>
                    Batch Attendance Analytics
                  </Typography>
                  <Typography sx={{ opacity: 0.85, fontSize: 14, mt: 0.5 }}>
                    {view === 'overview' && 'Batch, subject and student attendance from the batch academic calendar'}
                    {view === 'batch' && 'Subject wise, student wise and session wise attendance of this batch'}
                    {view === 'student' && 'Individual attendance across subjects, months and sessions'}
                  </Typography>
                </Box>
                {view !== 'overview' && (
                  <Button
                    onClick={() => (view === 'student' && batchId ? setSearchParams({ batch: batchId }) : goOverview())}
                    startIcon={<Icon icon="solar:arrow-left-linear" />}
                    sx={{ bgcolor: 'rgba(255,255,255,.16)', color: '#fff', border: '1px solid rgba(255,255,255,.25)', '&:hover': { bgcolor: 'rgba(255,255,255,.26)' } }}
                  >
                    {view === 'student' ? 'Back to batch' : 'All batches'}
                  </Button>
                )}
              </Stack>
              {loading && <LinearProgress sx={{ height: 3, bgcolor: 'transparent', '& .MuiLinearProgress-bar': { bgcolor: '#F0ABFC' } }} />}
            </Card>

            <Card sx={{ borderRadius: 4 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} alignItems={{ md: 'center' }} flexWrap="wrap" sx={{ p: 2 }}>
                {view === 'overview' && (
                  <>
                    <Autocomplete
                      size="small"
                      options={allcourses}
                      getOptionLabel={(option) => option?.course_name || ''}
                      value={allcourses.find((option) => option.course_available_id === course) || null}
                      onChange={(_, option) => setCourse(option?.course_available_id || '')}
                      sx={{ minWidth: { xs: '100%', md: 240 } }}
                      renderInput={(params) => <TextField {...params} label="Course" placeholder="All courses" />}
                    />
                    <TextField select size="small" label="Batch status" value={status} onChange={(event) => setStatus(event.target.value)} sx={{ minWidth: { xs: '100%', md: 180 } }}>
                      <MenuItem value="">All statuses</MenuItem>
                      {BATCH_STATUSES.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </>
                )}
                <TextField select size="small" label="Sessions in" value={range} onChange={(event) => setRange(event.target.value)} sx={{ minWidth: { xs: '100%', md: 170 } }}>
                  {RANGES.map((item) => (
                    <MenuItem key={item.key} value={item.key}>
                      {item.label}
                    </MenuItem>
                  ))}
                </TextField>
                {range === 'custom' && <FromAndToDatePicker fromDate={customFrom} toDate={customTo} setFromDate={setCustomFrom} setToDate={setCustomTo} disabled={loading} />}
                <Box sx={{ flex: 1 }} />
                <Stack direction="row" alignItems="center" gap={0.75} sx={{ color: RT.muted }}>
                  <Icon icon="solar:info-circle-linear" width={16} />
                  <Typography variant="caption">Attendance % = present ÷ (present + absent). N/A marks are left out.</Typography>
                </Stack>
              </Stack>
            </Card>

            {view === 'overview' && <OverviewView data={overview.data} loading={overview.loading} onOpenBatch={openBatch} onOpenStudent={openStudent} />}
            {view === 'batch' && <BatchView data={batch.data} loading={batch.loading} onOpenStudent={openStudent} />}
            {view === 'student' && <StudentView data={student.data} loading={student.loading} />}
          </Stack>
        </Box>
      </Page>
    </RetentionThemeProvider>
  );
}
